import { useSessionStorageState } from "ahooks"
import { CommandToSend } from "interface/device"
import { useCallback, useEffect } from "react"
import { useLogCommand } from "services/devices"
import {
	FTDIJson,
	getFTDIJsonBySerial,
	SendJsonCommandOptions,
} from "./ftdi-json"

export function useFootprint() {
	const isWebUsbAvailable = !!navigator.usb && !!navigator.usb.requestDevice
	const [state = DEFAULT_STATE, setState] = useSessionStorageState<
		Partial<State>
	>("footprint-web-usb", {
		listenStorageChange: true,
		defaultValue: DEFAULT_STATE,
	})
	const logCommand = useLogCommand()
	const handleLogCommand = useCallback(
		(command: CommandToSend) => {
			if (!state.serialNumber) {
				return
			}
			logCommand.mutate({
				deviceSerialNumber: state.serialNumber,
				data: {
					command,
				},
			})
		},
		[logCommand, state.serialNumber],
	)

	const requestAuthorizedDevice = useCallback(async () => {
		setState({
			...DEFAULT_STATE,
			executing: { DATAREAD: true },
		})
		try {
			const authorizedDevice = await navigator.usb.requestDevice({
				filters: [],
			})
			console.debug(
				"[useFootprint] requestAuthorizedDevice() Authorized device:",
				authorizedDevice,
			)
			const { data, success, message } = await sendUSBCommandWithFTDI<Device>({
				dongleSerialNumber: authorizedDevice.serialNumber,
				commandToSend: "DATAREAD",
				options: {
					quietWindowMilliseconds: 6_000,
				},
			})
			setState((prev) => ({
				...prev,
				dongleSerialNumber: authorizedDevice.serialNumber,
				serialNumber: data?.serialNumber ?? authorizedDevice.serialNumber,
				model: data?.model ?? authorizedDevice.productName,
				executing: { DATAREAD: false },
				hasError: !success,
				message,
			}))
		} catch (error) {
			console.error(
				"[useFootprint] requestAuthorizedDevice() USB device request cancelled or failed:",
				error,
			)
			setState({
				...DEFAULT_STATE,
				executing: { DATAREAD: false },
				hasError: true,
				message: error instanceof Error ? error.message : String(error),
			})
		} finally {
			handleLogCommand("DATAREAD")
		}
	}, [handleLogCommand, setState])

	const getWifiList = useCallback(async () => {
		setState((prev) => ({
			...prev,
			executing: { ...prev?.executing, WIFILIST: true },
			hasError: false,
			message: undefined,
		}))

		const wifiListResult = await sendUSBCommandWithFTDI<State["wifiList"]>({
			dongleSerialNumber: state.dongleSerialNumber!,
			commandToSend: "WIFILIST",
			options: {
				quietWindowMilliseconds: 16_000,
			},
		})

		handleLogCommand("WIFILIST")

		const dedupedWifiList = Array.isArray(wifiListResult.data)
			? dedupeWifiList(wifiListResult.data)
			: []

		let networkstatResult: DeviceResponse<ConnectedNetwork[]> = {
			success: false,
		}
		let connectedNetwork: ConnectedNetwork | undefined

		if (dedupedWifiList.length > 0) {
			networkstatResult = await sendUSBCommandWithFTDI<ConnectedNetwork[]>({
				dongleSerialNumber: state.dongleSerialNumber!,
				commandToSend: "NETWORKSTAT",
				options: {
					quietWindowMilliseconds: 16_000,
				},
			})

			handleLogCommand("NETWORKSTAT")

			connectedNetwork = (networkstatResult.data ?? []).find(
				({ type, state }) => type === "wifi" && state === "connected",
			)
		}

		setState((prev) => ({
			...prev,
			executing: { ...prev?.executing, WIFILIST: false },
			hasError: !wifiListResult.success || !networkstatResult.success,
			message: wifiListResult.success
				? networkstatResult.success
					? undefined
					: networkstatResult.message
				: wifiListResult.message,
			wifiList: dedupedWifiList,
			wifiSsid: connectedNetwork?.connection,
			isWifiConnected: !!connectedNetwork?.connection,
		}))
	}, [handleLogCommand, setState, state.dongleSerialNumber])

	const connectToWifi = useCallback(async () => {
		setState((prev) => ({
			...prev,
			executing: { ...prev?.executing, WIFICONN: true },
			hasError: false,
			message: undefined,
		}))

		const { success, message } = await sendUSBCommandWithFTDI({
			dongleSerialNumber: state.dongleSerialNumber!,
			commandToSend: `WIFICONN,${state.wifiSsid},${state.wifiPassword || ""}`,
			options: {
				quietWindowMilliseconds: 64_000,
				maxRetries: 6,
			},
		})

		handleLogCommand("WIFICONN")

		setState((prev) => ({
			...prev,
			executing: { ...prev?.executing, WIFICONN: false },
			hasError: !success,
			message,
			isWifiConnected: success,
		}))
	}, [
		handleLogCommand,
		setState,
		state.dongleSerialNumber,
		state.wifiPassword,
		state.wifiSsid,
	])

	const deleteWifi = useCallback(async () => {
		setState((prev) => ({
			...prev,
			executing: { ...prev?.executing, WIFIDELETE: true },
			hasError: false,
			message: undefined,
		}))

		const { success, message } = await sendUSBCommandWithFTDI({
			dongleSerialNumber: state.dongleSerialNumber!,
			commandToSend: `WIFIDELETE,${state.wifiSsid}`,
			options: {
				quietWindowMilliseconds: 64_000,
			},
		})

		handleLogCommand("WIFIDELETE")

		setState((prev) => ({
			...prev,
			executing: { ...prev?.executing, WIFIDELETE: false },
			hasError: !success,
			message,
			wifiSsid: undefined,
			wifiPassword: undefined,
			isWifiConnected: false,
		}))
	}, [handleLogCommand, setState, state.dongleSerialNumber, state.wifiSsid])

	const swUpdateProgressCheck = useCallback(
		async (versionOverride?: VersionData) => {
			const version = versionOverride || state.version
			if (!version) {
				console.warn(
					"[useFootprint] swUpdateProgressCheck() No version data found. Skipping.",
				)
				return
			}

			setState((prev) => ({
				...prev,
				executing: {
					...prev?.executing,
					SWUPDATETRIGGER: false,
					SWUPDATEPROGCHECK: true,
				},
				hasError: false,
				message: undefined,
			}))

			if (version.OS_VERSION_ORDERABLE >= "0.2.3z-dev+54-20250223T164310Z") {
				// New version of the command.
				const {
					status = SwUpdateStatusEnumV2025_07_01.SWUPDATE_START,
					success,
					message,
					data,
				} = await sendUSBCommandWithFTDI<number, SwUpdateStatusEnumV2025_07_01>(
					{
						dongleSerialNumber: state.dongleSerialNumber!,
						commandToSend: "SWUPDATEPROGCHECK",
					},
				)

				handleLogCommand("SWUPDATEPROGCHECK")

				setState((prev) => ({
					...prev,
					hasError: !success,
					message,
					executing: { ...prev?.executing, SWUPDATEPROGCHECK: false },
					progress: { ...prev?.progress, SWUPDATEPROGCHECK: data },
					isUpdateSuccess: [
						SwUpdateStatusEnumV2025_07_01.SWUPDATE_SUCCESS,
						SwUpdateStatusEnumV2025_07_01.SWUPDATE_NOT_IN_PROGRESS,
					].includes(status),
					isUpdating: [
						SwUpdateStatusEnumV2025_07_01.DOWNLOAD_IN_PROGRESS,
						SwUpdateStatusEnumV2025_07_01.INSTALLATION_IN_PROGRESS,
					].includes(status),
					isUpdateError: [
						SwUpdateStatusEnumV2025_07_01.SWUPDATE_FAILURE,
						SwUpdateStatusEnumV2025_07_01.SOCKET_ERROR,
					].includes(status),
				}))
			} else {
				// TODO: Remove this once we have a new version of the command available on all devices.
				// https://linear.app/empo-health/issue/RHL-2208/remove-swupdateprogress-check
				// Old version of the command.
				const {
					status = SwUpdateStatusEnumV2025_07_00.SWUPDATE_NOT_IN_PROGRESS,
					success,
					message,
					data,
				} = await sendUSBCommandWithFTDI<number, SwUpdateStatusEnumV2025_07_00>(
					{
						dongleSerialNumber: state.dongleSerialNumber!,
						commandToSend: "SWUPDATEPROGCHECK",
					},
				)

				handleLogCommand("SWUPDATEPROGCHECK")

				setState((prev) => ({
					...prev,
					hasError: !success,
					message,
					executing: { ...prev?.executing, SWUPDATEPROGCHECK: false },
					progress: { ...prev?.progress, SWUPDATEPROGCHECK: data },
					isUpdateSuccess: [
						SwUpdateStatusEnumV2025_07_00.SWUPDATE_SUCCESS,
						SwUpdateStatusEnumV2025_07_00.SWUPDATE_NOT_IN_PROGRESS,
					].includes(status),
					isUpdating: [
						SwUpdateStatusEnumV2025_07_00.DOWNLOAD_IN_PROGRESS,
						SwUpdateStatusEnumV2025_07_00.INSTALLATION_IN_PROGRESS,
					].includes(status),
					isUpdateError: [
						SwUpdateStatusEnumV2025_07_00.SWUPDATE_FAILURE,
						SwUpdateStatusEnumV2025_07_00.SOCKET_ERROR,
					].includes(status),
				}))
			}
		},
		[handleLogCommand, setState, state.dongleSerialNumber, state.version],
	)

	const softwareUpdate = useCallback(async () => {
		setState((prev) => ({
			...prev,
			executing: { ...prev?.executing, SWUPDATETRIGGER: true },
			hasError: false,
			message: undefined,
		}))

		const { success, message } = await sendUSBCommandWithFTDI({
			dongleSerialNumber: state.dongleSerialNumber!,
			commandToSend: "SWUPDATETRIGGER",
		})

		handleLogCommand("SWUPDATETRIGGER")

		const hasNoSoftwareUpdates = ["No SW Update Found"].includes(message)

		setState((prev) => ({
			...prev,
			executing: { ...prev?.executing, SWUPDATETRIGGER: false },
			hasError: !success,
			message,
			isUpdateSuccess: hasNoSoftwareUpdates,
		}))
	}, [handleLogCommand, setState, state.dongleSerialNumber])

	const getVersion = useCallback(async () => {
		setState((prev) => ({
			...prev,
			executing: { ...prev?.executing, VERSION: true },
			hasError: false,
			message: undefined,
		}))

		const { success, message, data } = await sendUSBCommandWithFTDINonJSON<
			VersionData,
			any
		>({
			dongleSerialNumber: state.dongleSerialNumber!,
			commandToSend: "VERSION",
			options: {
				quietWindowMilliseconds: 3_000,
			},
		})

		handleLogCommand("VERSION")

		setState((prev) => ({
			...prev,
			executing: { ...prev?.executing, VERSION: false },
			hasError: !success,
			message,
			version: data,
		}))
		return data
	}, [handleLogCommand, setState, state.dongleSerialNumber])

	useEffect(() => {
		if (!isWebUsbAvailable) {
			return
		}

		const handleConnect = async (event: USBConnectionEvent) => {
			console.debug(
				"[useFootprint] handleConnect() USB device connected:",
				event.device,
			)
			await requestAuthorizedDevice()
		}

		const handleDisconnect = async (event: USBConnectionEvent) => {
			console.debug(
				"[useFootprint] handleDisconnect() USB device disconnected:",
				event.device,
			)
			await requestAuthorizedDevice()
		}

		navigator.usb.addEventListener("connect", handleConnect)
		navigator.usb.addEventListener("disconnect", handleDisconnect)

		return () => {
			if (!isWebUsbAvailable) {
				return
			}

			navigator.usb.removeEventListener("connect", handleConnect)
			navigator.usb.removeEventListener("disconnect", handleDisconnect)
		}
	}, [isWebUsbAvailable, requestAuthorizedDevice])

	const actions = {
		requestAuthorizedDevice,
		getWifiList,
		connectToWifi,
		softwareUpdate,
		swUpdateProgressCheck,
		deleteWifi,
		resetState: () => setState(DEFAULT_STATE),
		setSerialNumber: (serialNumber: State["serialNumber"]) =>
			setState((prev) => ({ ...prev, serialNumber })),
		setWifiPassword: (wifiPassword: State["wifiPassword"]) =>
			setState((prev) => ({ ...prev, wifiPassword })),
		setWifiSsid: (wifiSsid: State["wifiSsid"]) =>
			setState((prev) => ({
				...prev,
				wifiSsid,
				wifiPassword: undefined,
				isWifiConnected: false,
			})),
		getVersion,
		clearError: () =>
			setState((prev) => ({
				...prev,
				hasError: false,
				message: undefined,
			})),
		close: async () => {
			if (!activeDevice) {
				console.warn(
					"[useFootprint] Trying to close FTDIJson device but no device found.",
					activeDevice,
				)
				return
			}
			try {
				console.debug(
					"[useFootprint] close() Trying to close FTDIJson device:",
					activeDevice,
				)
				await activeDevice.close()
			} catch (closeError) {
				console.error(
					"[useFootprint] Error closing FTDIJson device:",
					closeError,
				)
			} finally {
				setState((prev) => ({
					...prev,
					executing: {},
				}))
			}
		},
	}

	return {
		...state,
		...actions,
		isWebUsbAvailable,
	} as State & typeof actions & { isWebUsbAvailable: boolean }
}

const sendUSBCommandWithFTDI = async <D, S = undefined>({
	dongleSerialNumber,
	commandToSend,
	options,
}: {
	dongleSerialNumber: USBDevice["serialNumber"]
	commandToSend: CommandToSend
	options?: SendJsonCommandOptions
}): Promise<DeviceResponse<D, S>> => {
	if (!dongleSerialNumber) {
		return { success: false, message: "No serial number provided" }
	}
	console.group(
		`[useFootprint] sendUSBCommandWithFTDI() Sending command "${commandToSend}" to dongle ${dongleSerialNumber}`,
	)

	console.time(`[useFootprint] FTDIJson device creation time: ${commandToSend}`)

	let responseJson
	try {
		activeDevice = await getFTDIJsonBySerial(dongleSerialNumber, {
			baudRate: 115200,
		})

		responseJson = await activeDevice.sendJsonCommand<DeviceResponse<D, S>>(
			commandToSend + ",JSON",
			getNormalizedOptions(options),
		)

		console.debug("JSON response:", responseJson)

		return responseJson
	} catch (error: any) {
		console.error("Error or invalid JSON response:", error)

		return {
			success: false,
			message:
				"An unexpected error was encountered with the connection to the Footprint. Please unplug the data cable, plug it back in, and then try again.",
		}
	} finally {
		if (!activeDevice) {
			return {
				success: false,
				message: "No active device found.",
			} as DeviceResponse<D, S>
		}
		try {
			await activeDevice.close()
		} catch (closeError) {
			console.error("[useFootprint] Error closing FTDIJson device:", closeError)
		}
		console.timeEnd(
			`[useFootprint] FTDIJson device creation time: ${commandToSend}`,
		)
		console.groupEnd()
	}
}

const sendUSBCommandWithFTDINonJSON = async <D, S = undefined>({
	dongleSerialNumber,
	commandToSend,
	options,
}: {
	dongleSerialNumber: USBDevice["serialNumber"]
	commandToSend: CommandToSend
	options?: SendJsonCommandOptions
}): Promise<DeviceResponse<D, S>> => {
	if (!dongleSerialNumber) {
		return { success: false, message: "No serial number provided" }
	}
	console.group(
		`[useFootprint] sendUSBCommandWithFTDINonJSON() Sending command "${commandToSend}" to dongle ${dongleSerialNumber}`,
	)

	console.time(`[useFootprint] FTDIJson device creation time: ${commandToSend}`)
	try {
		activeDevice = await getFTDIJsonBySerial(dongleSerialNumber, {
			baudRate: 115200,
		})

		const responseJson = await activeDevice.sendCommand<DeviceResponse<D, S>>(
			commandToSend,
			getNormalizedOptions(options),
		)

		console.debug("JSON response:", responseJson)

		return responseJson
	} catch (error: any) {
		console.error("Error or invalid JSON response:", error)

		return {
			success: false,
			message:
				"An unexpected error was encountered with the connection to the Footprint. Please unplug the data cable, plug it back in, and then try again.",
		}
	} finally {
		if (!activeDevice) {
			return {
				success: false,
				message: "No active device found.",
			} as DeviceResponse<D, S>
		}
		try {
			await activeDevice.close()
		} catch (closeError) {
			console.error("[useFootprint] Error closing FTDIJson device:", closeError)
		}
		console.timeEnd(
			`[useFootprint] FTDIJson device creation time: ${commandToSend}`,
		)
		console.groupEnd()
	}
}

function dedupeWifiList(list: State["wifiList"]) {
	const ssidMap = new Map<string, State["wifiList"][number]>()
	for (const entry of list) {
		if (!entry.ssid) continue // Skip empty SSIDs
		if (
			!ssidMap.has(entry.ssid) ||
			entry.signal > ssidMap.get(entry.ssid)!.signal
		) {
			ssidMap.set(entry.ssid, entry)
		}
	}
	return Array.from(ssidMap.values())
}

function getNormalizedOptions({
	quietWindowMilliseconds,
	responseTimeoutMilliseconds,
	...options
}: SendJsonCommandOptions = {}) {
	return {
		...options,
		quietWindowMilliseconds:
			import.meta.env.STORYBOOK === "true" || !!window.__IS_PLAYWRIGHT__
				? 100
				: quietWindowMilliseconds,
		responseTimeoutMilliseconds:
			import.meta.env.STORYBOOK === "true"
				? 1_000
				: responseTimeoutMilliseconds,
	}
}

let activeDevice: FTDIJson | null = null

interface DeviceResponse<Data, Status = undefined> {
	version?: string
	success: boolean
	message?: any
	status?: Status
	data?: Data
}

interface Device {
	serialNumber: string
	model: string
}

interface ConnectedNetwork {
	device: string
	type: "wifi" | "gsm"
	state: "connected" | "disconnected" | "unmanaged"
	connection: string
}
interface VersionData {
	OS_VERSION: string
	OS_VERSION_ORDERABLE: string
	SCANNER_FW_VERSION: string
	SCANNER_SANE_VERSION: string
	APPLICATION_VERSION: string
}
interface State {
	dongleSerialNumber: USBDevice["serialNumber"]
	hasError: boolean
	message?: any
	serialNumber: Device["serialNumber"] | null
	model: Device["model"] | null
	wifiList: { ssid: string; signal: number }[]
	wifiSsid?: string
	wifiPassword?: string
	isWifiConnected?: boolean
	executing: Partial<Record<CommandToSend, boolean>>
	progress: Partial<Record<CommandToSend, number>>
	isUpdating?: boolean
	isUpdateError?: boolean
	isUpdateSuccess?: boolean
	version: VersionData | undefined
}

const DEFAULT_STATE: State = {
	dongleSerialNumber: null,
	serialNumber: null,
	model: null,
	hasError: false,
	message: undefined,
	wifiList: [],
	executing: {},
	progress: {},
	version: undefined,
}

// TODO: Remove this once we have a new version of the command available on all devices.
// https://linear.app/empo-health/issue/RHL-2208/remove-swupdateprogress-check
export enum SwUpdateStatusEnumV2025_07_00 {
	SWUPDATE_NOT_IN_PROGRESS = 0,
	DOWNLOAD_IN_PROGRESS = 1,
	INSTALLATION_IN_PROGRESS = 2,
	SWUPDATE_SUCCESS = 3,
	SWUPDATE_FAILURE = 4,
	SOCKET_ERROR = 5,
}

export enum SwUpdateStatusEnumV2025_07_01 {
	SWUPDATE_START = 0,
	SWUPDATE_NOT_IN_PROGRESS = 1,
	DOWNLOAD_IN_PROGRESS = 2,
	INSTALLATION_IN_PROGRESS = 3,
	SWUPDATE_SUCCESS = 4,
	SWUPDATE_FAILURE = 5,
	SOCKET_ERROR = 6,
}
